/**
 * Bulk Import/Export Dialog Component
 *
 * Dialog for importing parts from CSV/Excel and exporting inventory to various formats.
 * Supports validation, preview, and error handling for bulk operations.
 */

import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  Alert,
  Paper,
  List,
  ListItem,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import CloseIcon from '@mui/icons-material/Close';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import TableViewIcon from '@mui/icons-material/TableView';
import type { InventoryItem } from '@/types';
import { useImportParts, type ImportResult } from '@/hooks/useInventory';
import { useNotify } from '@/stores/notificationStore';

interface BulkImportExportDialogProps {
  open: boolean;
  onClose: () => void;
  items: InventoryItem[];
  onImport: (items: Record<string, unknown>[]) => void;
}

type TabValue = 'import' | 'export';
type ExportFormat = 'csv' | 'excel' | 'json';
type ImportStatus = 'idle' | 'parsing' | 'validating' | 'preview' | 'importing' | 'success' | 'error';

interface ValidationError {
  row: number;
  field?: string;
  message: string;
}

interface ImportPreview {
  validItems: Record<string, unknown>[];
  errors: ValidationError[];
  warnings: ValidationError[];
}

/** Stringify an unknown parsed-cell value for display. */
function toDisplay(value: unknown): string {
  return value === undefined || value === null ? '' : String(value);
}

/** First non-empty value among the given keys of a parsed row. */
function pickField(row: Record<string, unknown>, keys: string[]): unknown {
  for (const key of keys) {
    const value = row[key];
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return undefined;
}

export function BulkImportExportDialog({
  open,
  onClose,
  items,
  onImport,
}: BulkImportExportDialogProps) {
  const { t } = useTranslation('inventory');
  const notify = useNotify();
  const { t: tc } = useTranslation('common');
  const [tab, setTab] = useState<TabValue>('import');
  const [importStatus, setImportStatus] = useState<ImportStatus>('idle');
  const [exportFormat, setExportFormat] = useState<ExportFormat>('csv');
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const importMutation = useImportParts();

  const handleTabChange = (_: React.SyntheticEvent, newValue: TabValue) => {
    setTab(newValue);
    // Reset import state when switching tabs
    if (newValue === 'export') {
      setImportStatus('idle');
      setPreview(null);
      setResult(null);
      setSelectedFile(null);
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setSelectedFile(file);
    setResult(null);
    setImportStatus('parsing');

    try {
      // 1. Parse the file client-side into a list of raw objects. The backend
      //    validates every row against the part schema and upserts by SKU.
      const text = await file.text();
      let parsedItems: Record<string, unknown>[];

      if (file.name.toLowerCase().endsWith('.json')) {
        const parsed: unknown = JSON.parse(text);
        if (!Array.isArray(parsed)) throw new Error('JSON root must be an array');
        parsedItems = parsed as Record<string, unknown>[];
      } else {
        // CSV: header row maps column name → value for each data row.
        const lines = text.split('\n').filter((line) => line.trim().length > 0);
        const headerLine = lines[0];
        if (!headerLine) throw new Error('Empty file');
        const headers = headerLine.split(',').map((header) => header.trim());
        parsedItems = lines.slice(1).map((line) => {
          const values = line.split(',');
          const obj: Record<string, unknown> = {};
          headers.forEach((header, index) => {
            obj[header] = values[index]?.trim();
          });
          return obj;
        });
      }

      // 2. Show a preview. Validation errors are reported by the backend on
      //    import, so the preview starts with no errors/warnings.
      setPreview({ validItems: parsedItems, errors: [], warnings: [] });
      setImportStatus('preview');
    } catch {
      setImportStatus('error');
    }
  };

  const handleImport = async () => {
    if (!preview) return;

    setImportStatus('importing');

    try {
      const importResult = await importMutation.mutateAsync(preview.validItems);
      setResult(importResult);

      if (importResult.errors.length > 0) {
        // Partial import: surface the per-row errors and stay on the preview.
        setPreview((prev) => (prev ? { ...prev, errors: importResult.errors } : prev));
        setImportStatus('preview');
      } else {
        onImport(preview.validItems);
        setImportStatus('success');
        setTimeout(() => {
          handleClose();
        }, 1500);
      }
    } catch {
      setImportStatus('error');
    }
  };

  const handleExport = () => {
    // Create CSV content
    let content = '';
    let filename = '';
    let mimeType = '';

    switch (exportFormat) {
      case 'csv': {
        const headers = [
          t('bulkImport.colPartNumber'),
          t('bulkImport.colName'),
          t('bulkImport.colCategory'),
          t('bulkImport.colBrand'),
          t('bulkImport.colQuantity'),
          t('bulkImport.colMinLevel'),
          t('bulkImport.colMaxLevel'),
          t('bulkImport.colUnitPrice'),
          t('bulkImport.colCostPrice'),
          t('bulkImport.colLocation'),
          t('bulkImport.colBarcode'),
        ].join(',');

        const rows = items.map((item) =>
          [
            item.partNumber,
            `"${item.name}"`,
            item.categoryName ?? '',
            item.brand || '',
            item.quantity,
            item.minStockLevel,
            item.maxStockLevel || '',
            (item.unitPriceCents / 100).toFixed(2),
            (item.costPriceCents / 100).toFixed(2),
            item.location || '',
            item.barcode || '',
          ].join(',')
        );

        content = [headers, ...rows].join('\n');
        filename = `inventory_${new Date().toISOString().split('T')[0]}.csv`;
        mimeType = 'text/csv;charset=utf-8;';
        break;
      }

      case 'json': {
        content = JSON.stringify(items, null, 2);
        filename = `inventory_${new Date().toISOString().split('T')[0]}.json`;
        mimeType = 'application/json;charset=utf-8;';
        break;
      }

      case 'excel': {
        // In production, use a library like SheetJS (xlsx).
        // notify, not alert(): a native modal is unstyled OS chrome and
        // blocks the page (#2726).
        notify.info(t('bulkImport.excelComingSoon'));
        return;
      }
    }

    // Create download
    const blob = new Blob([content], { type: mimeType });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const handleClose = () => {
    setImportStatus('idle');
    setPreview(null);
    setResult(null);
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onClose();
  };

  const getImportStatusMessage = () => {
    switch (importStatus) {
      case 'parsing':
        return t('bulkImport.statusParsing');
      case 'validating':
        return t('bulkImport.statusValidating');
      case 'importing':
        return t('bulkImport.statusImporting');
      case 'success':
        return t('bulkImport.statusSuccess');
      case 'error':
        return t('bulkImport.statusError');
      default:
        return '';
    }
  };

  return (
    <ResponsiveDialog mobileTitle={t('bulkImport.title')} open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">{t('bulkImport.title')}</Typography>
          <Tooltip title={tc('close')}>
            <IconButton onClick={handleClose} size="small" aria-label={tc('close')}>
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </DialogTitle>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
        <Tabs value={tab} onChange={handleTabChange}>
          <Tab value="import" icon={<UploadFileIcon />} label={t('bulkImport.importTab')} iconPosition="start" />
          <Tab value="export" icon={<DownloadIcon />} label={t('bulkImport.exportTab')} iconPosition="start" />
        </Tabs>
      </Box>

      <DialogContent sx={{ minHeight: 400 }}>
        {tab === 'import' && (
          <Box>
            {(importStatus === 'idle' || importStatus === 'error') && (
              <Box>
                {importStatus === 'error' && (
                  <Alert severity="error" sx={{ mb: 3 }}>
                    {t('bulkImport.parseError')}
                  </Alert>
                )}
                <Alert severity="info" sx={{ mb: 3 }}>
                  {t('bulkImport.importInfo')}
                </Alert>

                <Paper
                  variant="outlined"
                  sx={{
                    p: 4,
                    textAlign: 'center',
                    border: '2px dashed',
                    borderColor: 'divider',
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: 'action.hover',
                      borderColor: 'primary.main',
                    },
                  }}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <UploadFileIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    {t('bulkImport.selectFile')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {t('bulkImport.supportedFormats')}
                  </Typography>
                  <Button variant="outlined" sx={{ mt: 2 }}>
                    {t('bulkImport.browseFiles')}
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,.xlsx,.xls,.json"
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                  />
                </Paper>

                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    {t('bulkImport.requiredFields')}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {[
                      t('bulkImport.colPartNumber'),
                      t('bulkImport.colName'),
                      t('bulkImport.colCategory'),
                      t('bulkImport.colUnitPrice'),
                      t('bulkImport.colCostPrice'),
                      t('bulkImport.colQuantity'),
                      t('bulkImport.colMinLevel'),
                    ].map(
                      (field) => (
                        <Chip key={field} label={field} size="small" />
                      )
                    )}
                  </Box>

                  <Button
                    variant="text"
                    size="small"
                    startIcon={<DownloadIcon />}
                    sx={{ mt: 2 }}
                    onClick={() => {
                      // Download template
                      const template = [
                        t('bulkImport.colPartNumber'),
                        t('bulkImport.colName'),
                        t('bulkImport.colCategory'),
                        t('bulkImport.colUnitPrice'),
                        t('bulkImport.colCostPrice'),
                        t('bulkImport.colQuantity'),
                        t('bulkImport.colMinLevel'),
                      ].join(',') + '\n';
                      const blob = new Blob([template], { type: 'text/csv;charset=utf-8;' });
                      const link = document.createElement('a');
                      link.href = URL.createObjectURL(blob);
                      link.download = 'inventory_template.csv';
                      link.click();
                      URL.revokeObjectURL(link.href);
                    }}
                  >
                    {t('bulkImport.downloadTemplate')}
                  </Button>
                </Box>
              </Box>
            )}

            {(importStatus === 'parsing' || importStatus === 'validating' || importStatus === 'importing') && (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  {selectedFile && (
                    <>
                      <InsertDriveFileIcon color="primary" />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" fontWeight={500}>
                          {selectedFile.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {(selectedFile.size / 1024).toFixed(1)} KB
                        </Typography>
                      </Box>
                    </>
                  )}
                </Box>
                <LinearProgress sx={{ mb: 2 }} />
                <Typography variant="body2" color="text.secondary" align="center">
                  {getImportStatusMessage()}
                </Typography>
              </Box>
            )}

            {importStatus === 'preview' && preview && (
              <Box>
                <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                  <Paper variant="outlined" sx={{ flex: 1, p: 2, textAlign: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
                      <CheckCircleIcon color="success" />
                      <Typography variant="h5" fontWeight={600}>
                        {preview.validItems.length}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {t('bulkImport.validRows')}
                    </Typography>
                  </Paper>

                  <Paper variant="outlined" sx={{ flex: 1, p: 2, textAlign: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
                      <ErrorIcon color="error" />
                      <Typography variant="h5" fontWeight={600}>
                        {preview.errors.length}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {t('bulkImport.errors')}
                    </Typography>
                  </Paper>

                  <Paper variant="outlined" sx={{ flex: 1, p: 2, textAlign: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
                      <WarningIcon color="warning" />
                      <Typography variant="h5" fontWeight={600}>
                        {preview.warnings.length}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {t('bulkImport.warnings')}
                    </Typography>
                  </Paper>
                </Box>

                {result && (result.created > 0 || result.updated > 0) && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    {t('bulkImport.resultSummary', { created: result.created, updated: result.updated })}
                  </Alert>
                )}

                {preview.errors.length > 0 && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      {t('bulkImport.errorsCount', { count: preview.errors.length })}
                    </Typography>
                    <List dense>
                      {preview.errors.slice(0, 5).map((error, idx) => (
                        <ListItem key={idx} disablePadding>
                          <ListItemText
                            primary={
                              error.field
                                ? t('bulkImport.rowError', { row: error.row, field: error.field })
                                : t('bulkImport.rowErrorNoField', { row: error.row })
                            }
                            secondary={error.message}
                            primaryTypographyProps={{ variant: 'caption', fontWeight: 600 }}
                            secondaryTypographyProps={{ variant: 'caption' }}
                          />
                        </ListItem>
                      ))}
                      {preview.errors.length > 5 && (
                        <Typography variant="caption" color="text.secondary">
                          {t('bulkImport.moreErrors', { count: preview.errors.length - 5 })}
                        </Typography>
                      )}
                    </List>
                  </Alert>
                )}

                {preview.warnings.length > 0 && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    <Typography variant="subtitle2">
                      {t('bulkImport.warningsCount', { count: preview.warnings.length })}
                    </Typography>
                  </Alert>
                )}

                {preview.validItems.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      {t('bulkImport.previewTitle', { count: preview.validItems.length })}
                    </Typography>
                    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell>{t('bulkImport.colPartNumber')}</TableCell>
                            <TableCell>{t('bulkImport.colName')}</TableCell>
                            <TableCell>{t('bulkImport.colCategory')}</TableCell>
                            <TableCell align="right">{t('bulkImport.colQuantity')}</TableCell>
                            <TableCell align="right">{t('bulkImport.colPrice')}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {preview.validItems.map((item, idx) => (
                            <TableRow key={idx}>
                              <TableCell>
                                <Typography variant="caption" fontFamily="monospace">
                                  {toDisplay(pickField(item, ['sku', 'partNumber']))}
                                </Typography>
                              </TableCell>
                              <TableCell>{toDisplay(pickField(item, ['name']))}</TableCell>
                              <TableCell>{toDisplay(pickField(item, ['categoryId', 'category']))}</TableCell>
                              <TableCell align="right">
                                {toDisplay(pickField(item, ['quantity', 'reorderPoint']))}
                              </TableCell>
                              <TableCell align="right">
                                €{((Number(pickField(item, ['sellPriceCents', 'unitPriceCents'])) || 0) / 100).toFixed(2)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}
              </Box>
            )}

            {importStatus === 'success' && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  {t('bulkImport.statusSuccess')}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {t('bulkImport.importedCount', {
                    count: (result?.created ?? 0) + (result?.updated ?? 0),
                  })}
                </Typography>
                {result && (
                  <Typography variant="caption" color="text.secondary">
                    {t('bulkImport.resultSummary', { created: result.created, updated: result.updated })}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        )}

        {tab === 'export' && (
          <Box>
            <Alert severity="info" sx={{ mb: 3 }}>
              {t('bulkImport.exportInfo')}
            </Alert>

            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>{t('bulkImport.fileFormat')}</InputLabel>
              <Select
                value={exportFormat}
                label={t('bulkImport.fileFormat')}
                onChange={(e) => setExportFormat(e.target.value as ExportFormat)}
              >
                <MenuItem value="csv">
                  <Box>
                    <Typography variant="body2">{t('bulkImport.formatCsv')}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('bulkImport.formatCsvDesc')}
                    </Typography>
                  </Box>
                </MenuItem>
                <MenuItem value="json">
                  <Box>
                    <Typography variant="body2">JSON</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('bulkImport.formatJsonDesc')}
                    </Typography>
                  </Box>
                </MenuItem>
                <MenuItem value="excel">
                  <Box>
                    <Typography variant="body2">Excel (.xlsx)</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('bulkImport.comingSoon')}
                    </Typography>
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>

            <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
              <TableViewIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {t('bulkImport.readyToExport')}
              </Typography>
              <Typography variant="h5" fontWeight={600} gutterBottom>
                {t('bulkImport.partsCount', { count: items.length })}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('bulkImport.estimatedSize', { size: Math.ceil(items.length / 10) })}
              </Typography>
            </Paper>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} color="inherit">
          {importStatus === 'success' ? tc('close') : tc('cancel')}
        </Button>
        {tab === 'import' && importStatus === 'preview' && preview && (
          <Button
            variant="contained"
            onClick={handleImport}
            disabled={preview.errors.length > 0}
            startIcon={<UploadFileIcon />}
          >
            {t('bulkImport.importCount', { count: preview.validItems.length })}
          </Button>
        )}
        {tab === 'export' && (
          <Button variant="contained" onClick={handleExport} startIcon={<DownloadIcon />}>
            {t('bulkImport.exportFile')}
          </Button>
        )}
      </DialogActions>
    </ResponsiveDialog>
  );
}
